<template>
  <div class="gs-app gm-leads-imports-catalog gm-refresh-tables">
    <!-- Loader -->
    <portal to="loaderTarget">
      <div class="absolute bg-grey-100 bg-opacity-50 h-full left-0 top-0 w-full z-99" v-if="isLoading">
        <gd-loader :label="$t('frontend_loading')"/>
      </div>
    </portal>
    
    <div class="flex mb-4 mx-4 gm-leads-imports-catalog__shell">
      <div class="w-full gm-leads-imports-catalog__workspace">
        <navigation-tabs
          class="gm-nav-suppresion-tabs absolute"
          :nav_tabs="navTabs"
        />

        <import-listing
          class="groovemail-import-users-listing-table gs-refresh-controls"
          ref="importListing"
          importSourceUrl="/imports-listing"
          importTableTitle="frontend_groovemail_imported_leads_listing_page_title"
          :importTableColumns="importedUsersTableColumns"
          :importTableOptions="importedUsersTableOptions"
          :showEmptyState="true"
          @showLoader="showLoader"
          @hideLoader="hideLoader"
        />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import auth from '@groovepages/gd-lib/auth';
import themeConfig from '@/theme/themeConfig.js';
import ImportListing from '@/components/import-listing.vue';
import NavigationTabs from '@/components/navigation-tabs.vue';
import { showRequestErrorAlert } from '@/components/showDialog.js';

export default {
  middleware: 'auth',
  metaInfo() {
    return {
      title: this.$t('frontend_groovemail_imported_leads_listing_page_title'),
    };
  },

  data() {
    return {
      fullPageLoader: true,
      isLoading: false,
      percentSwitch: false,
      loaderColor: themeConfig.appPrimaryColor,
      importedUsersTableColumns: [
        'csv_name',
        'total_leads',
        'created_at',
        'approved',
        'reject_reason'
      ],
    };
  },

  computed: {
    /**
     * Returns navigation tabs data for imported leads
     * @return {array} navigation tabs data
     */
     navTabs() {
      return [
        {
          tabName: 'frontend_groovemail_components_pricepoint_sidebar_leads',
          tabRoute: 'Leads2',
        },
        {
          tabName: 'frontend_groovemail_imported_leads_listing_page_imports_nav',
          tabRoute: 'ImportedLeadsListing',
        },
        {
          tabName: 'frontend_groovemail_components_sidebar_suppressions',
          tabRoute: 'Suppressions',
          tabQuery: { type: 'unsubscribes' },
        }
      ];
    },

    // new plans based limit check
    canImportLeads() {
      return auth.fetchFeatureValue('groovemail_can_import_contacts') === 1;
    },

    // Returns an object that contains the options for the imported users table
    importedUsersTableOptions() {
      return {
        perPage: 10,
        debounce: 1000,
        alwaysShowPerPageSelect: true,
        perPageValues: [10, 25, 50, 100],
        pagination: { chunk: 5 },
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc',
        },
        texts: {
          filterPlaceholder: this.$t('frontend_groovemail_tables_search_query'),
        },
        sortable: ['csv_name', 'total_leads', 'created_at'],
        filterable: ['csv_name'],
        headings: {
          csv_name: () => this.$t('frontend_groovemail_admin_import_user_approval_csv_name_header'),
          approved: () => this.$t('frontend_groovemail_leads_filter_status_option'),
          total_leads: () => this.$t('frontend_groovemail_admin_import_user_approval_total_leads_header'),
          created_at: () => this.$t('frontend_groovemail_admin_import_user_approval_file_upload_date_header'),
        },
        async requestFunction(data) {
          const self = this;
          if (!(data && data.orderBy)) {
            data.orderBy = 'created_at';
            data.ascending = 0;
          }
          return axios
            .get(self.url, {
              params: data,
            })
            .catch((e) => {
              self.dispatch('error', e);
            });
        },
        responseAdapter({ data }) {
          return {
            data: data.data,
            count: data.total,
          };
        },
      };
    },
  },

  components: {
    ImportListing,
    NavigationTabs,
  },

  async created() {
    // show loader
    this.showLoader();
    try {
      const dashboardData = await axios.get('/dashboard');
      if (dashboardData.data.can_import !== 1 || !this.canImportLeads) {
        this.$router.replace({ name: 'dashboard' }).catch(() => {});
      }
    } catch (error) {
      // hide loader
      this.hideLoader();

      showRequestErrorAlert(error.response?.data?.error);
    }
  },

  methods: {
    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },

    hideLoader() {
      if (this.isLoading) {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style lang="scss">
.groovemail-import-users-listing-table {
  .VueTables--server table {
    overflow: auto !important;
    th:nth-child(1),
    td:nth-child(1),
    th:nth-child(3),
    td:nth-child(3) {
      width: 27%;
    }
    th:nth-child(2),
    td:nth-child(2),
    th:nth-child(4),
    td:nth-child(4) {
      width: 20%;
    }
  }

  .icon-btn-width {
    width: 32px !important;
  }
}
</style>

<style scoped>
.gm-leads-imports-catalog {
  min-height: 100%;
  padding: 28px;
  color: var(--gs-foreground);
  background: var(--gs-background);
}

.gm-leads-imports-catalog__shell {
  margin: 0;
}

.gm-leads-imports-catalog__workspace {
  min-width: 0;
  padding: 20px 24px 24px;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

@media (max-width: 640px) {
  .gm-leads-imports-catalog { padding: 18px 12px 32px; }
  .gm-leads-imports-catalog__workspace { padding: 16px; }
}
</style>
